/**
 * Company Dogs Carousel — interaction tracking
 *
 * Emits two events via the theme's standard Shopify.analytics.publish('rudderstack_track', …) channel:
 *
 *   See All Company Dogs Link Clicked  — the header's "See all" link (or the polaroid style's button)
 *   Company Dogs Carousel Scrolled     — first time the row moves, once per page view
 *   Company Dog Card Flipped           — a polaroid card turned to its back (blurb + favorite toy)
 *
 * Also drives the polaroid card flip (card_style: polaroid, snippets/company-dog-polaroid-card.liquid): "+" turns
 * the photo over to the back, "−" turns it back. The face that isn't showing is made `inert`, and focus moves to
 * the button on the newly visible face so keyboard users don't lose their place.
 *
 * Not handled here: the favorite toy link. Any anchor pointing at /products/:handle is already tracked
 * as 'Product Link Clicked' by the global delegated listener in snippets/product-clicked-handler.liquid,
 * which also reports section_type, so the two surfaces are already distinguishable. Adding an event here
 * would double-count it.
 *
 * Property names are snake_case, matching the payload convention in snippets/age-gate-overlay.liquid.
 * Note that 'Product Link Clicked' uses camelCase — that event belongs to the shared global handler and
 * is deliberately left alone.
 */
(function () {
  'use strict';

  /* The homepage carousel and the Company Dogs page grid both use the polaroid card, so both bind here. The
     carousel-only listeners (See all, scroll) simply find nothing to bind to on the page grid. */
  var SECTION_SELECTOR = '.shopify-section--company-dogs-carousel, .shopify-section--company-dogs';

  function publish(eventName, eventData) {
    if (
      window.Shopify &&
      window.Shopify.analytics &&
      typeof window.Shopify.analytics.publish === 'function'
    ) {
      window.Shopify.analytics.publish('rudderstack_track', {
        eventName: eventName,
        eventData: eventData,
      });
    }
  }

  /* Mirrors the accessors used by snippets/product-clicked-handler.liquid so the events line up. */
  function getPageType() {
    return (
      (window.themeVariables &&
        window.themeVariables.settings &&
        window.themeVariables.settings.pageType) ||
      'unknown'
    );
  }

  function sectionContext(section) {
    var heading = section.querySelector('h2');

    return {
      section_id: section.id || null,
      section_type: section.classList.contains('shopify-section--company-dogs-carousel')
        ? 'company-dogs-carousel'
        : 'company-dogs',
      section_header: heading ? heading.textContent.trim() : null,
      page_type: getPageType(),
      page_path: window.location.pathname,
      subscriber_type: window.subscriberType || [],
    };
  }

  function trackSeeAll(section) {
    var link = section.querySelector('[data-company-dogs-see-all]');

    if (!link) {
      return;
    }

    link.addEventListener('click', function () {
      var payload = sectionContext(section);

      /* Read the label off the data attribute rather than textContent — the anchor also contains the
         chevron icon span, so textContent would carry its whitespace. */
      payload.link_text = link.getAttribute('data-company-dogs-see-all') || null;
      payload.link_url = link.getAttribute('href') || null;

      publish('See All Company Dogs Link Clicked', payload);
    });
  }

  function trackScroll(section) {
    var carousel = section.querySelector('company-dogs-carousel');

    if (!carousel) {
      return;
    }

    var hasFired = false;
    /* The carousel opens centred on a middle dog via `initial-index`; settling there is the page loading,
       not a visitor scrolling. */
    var restingIndex = parseInt(carousel.getAttribute('initial-index') || '0', 10);

    /* `carousel:settle` is dispatched by the theme's scroll-carousel element once a scroll has come to
       rest, covering swipes and arrow clicks alike, and it is already debounced. A raw scroll listener
       would fire dozens of times per swipe. */
    carousel.addEventListener('carousel:settle', function (event) {
      if (hasFired) {
        return;
      }

      var index = event.detail && typeof event.detail.index === 'number' ? event.detail.index : null;

      /* The resting start position is not an interaction. */
      if (index === null || index === restingIndex) {
        return;
      }

      hasFired = true;

      var payload = sectionContext(section);
      payload.slide_index = index;
      payload.badge_count = section.querySelectorAll('.company-dog-polaroid').length;

      publish('Company Dogs Carousel Scrolled', payload);
    });
  }

  function setFlipped(card, flipped) {
    var front = card.querySelector('.company-dog-polaroid__face--front');
    var back = card.querySelector('.company-dog-polaroid__face--back');
    var flipButton = card.querySelector('[data-company-dog-flip]');
    var unflipButton = card.querySelector('[data-company-dog-unflip]');

    if (!front || !back) {
      return;
    }

    card.classList.toggle('is-flipped', flipped);
    front.inert = flipped;
    back.inert = !flipped;

    if (flipButton) {
      flipButton.setAttribute('aria-expanded', flipped ? 'true' : 'false');
    }

    var target = flipped ? unflipButton : flipButton;

    if (target) {
      /* preventScroll: moving focus mustn't jolt the carousel to a different card. */
      target.focus({ preventScroll: true });
    }
  }

  function bindFlip(section) {
    section.addEventListener('click', function (event) {
      var flipButton = event.target.closest('[data-company-dog-flip]');
      var unflipButton = event.target.closest('[data-company-dog-unflip]');
      var button = flipButton || unflipButton;

      if (!button) {
        return;
      }

      var card = button.closest('[data-company-dog-polaroid]');

      if (!card) {
        return;
      }

      setFlipped(card, Boolean(flipButton));

      if (flipButton) {
        var nameEl = card.querySelector('.company-dog-polaroid__name');
        var payload = sectionContext(section);

        payload.dog_name = nameEl ? nameEl.textContent.trim() : null;
        publish('Company Dog Card Flipped', payload);
      }
    });
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    /* On shopify:section:load the root is the section wrapper itself, which querySelectorAll never returns. */
    var sections = scope.matches && scope.matches(SECTION_SELECTOR) ? [scope] : scope.querySelectorAll(SECTION_SELECTOR);

    Array.prototype.forEach.call(sections, function (section) {
      /* Per-element guard rather than a global one. The script tag lives inside the section, so it runs
         again for a second instance of the section on the same page, and again whenever the theme editor
         re-renders the section. Flagging the element binds each one exactly once while still letting a
         freshly injected section get bound. */
      if (section.dataset.companyDogsTracked) {
        return;
      }

      section.dataset.companyDogsTracked = 'true';

      trackSeeAll(section);
      trackScroll(section);
      bindFlip(section);
    });
  }

  /* <company-dogs-carousel>: the theme's <scroll-carousel> (assets/theme.js.liquid) adapted to centre-snapped cards.
     The base element picks the selected slide, and scrolls to one, as if slides snapped to the start of the row,
     so with centred cards its index drifted from the card actually in the middle: an arrow could move the row the
     wrong way, and the jump to the middle dog on load read as a visitor scroll. Its arrows also stayed live when
     every card already fits on screen. This keeps the base element's events (carousel:select / change / settle)
     but tracks the card nearest the middle, steps each arrow to the next card that actually moves the row, and
     hides an arrow at its end of the row — both when the row doesn't scroll at all. */
  function defineCarousel(ScrollCarousel) {
    if (window.customElements.get('company-dogs-carousel')) {
      return;
    }

    var CompanyDogsCarousel = class extends ScrollCarousel {
      connectedCallback() {
        super.connectedCallback();

        this._prevButton = document.querySelector('[data-company-dogs-prev][aria-controls="' + this.id + '"]');
        this._nextButton = document.querySelector('[data-company-dogs-next][aria-controls="' + this.id + '"]');

        var signal = this._abortController.signal;
        var self = this;

        if (this._prevButton) {
          this._prevButton.addEventListener('click', function () { self.previous(); }, { signal: signal });
        }

        if (this._nextButton) {
          this._nextButton.addEventListener('click', function () { self.next(); }, { signal: signal });
        }

        this._resizeObserver = new ResizeObserver(this._updateArrows.bind(this));
        this._resizeObserver.observe(this);
        this._updateArrows();
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        this._resizeObserver.disconnect();
      }

      get maxScroll() {
        return this.scrollWidth - this.clientWidth;
      }

      /* The scroll position that centres `slide`, clamped to what the row can actually scroll. */
      _scrollTargetFor(slide) {
        var slideLeft = slide.getBoundingClientRect().left - this.getBoundingClientRect().left + this.scrollLeft;
        var target = slideLeft + slide.offsetWidth / 2 - this.clientWidth / 2;

        return Math.min(Math.max(target, 0), this.maxScroll);
      }

      _centredIndex() {
        var middle = this.getBoundingClientRect().left + this.clientWidth / 2;
        var closest = null;
        var closestDistance = Infinity;

        this.visibleItems.forEach(function (item) {
          var rect = item.getBoundingClientRect();
          var distance = Math.abs(rect.left + rect.width / 2 - middle);

          if (distance < closestDistance) {
            closest = item;
            closestDistance = distance;
          }
        });

        return this.items.indexOf(closest);
      }

      /* Same as the base select() minus its start-aligned clamp to `totalPages`. */
      select(index, { animate = true, force = false } = {}) {
        var indexBeforeChange = this.selectedIndex;

        this._selectedIndex = index;

        if (!this.offsetParent || this.maxScroll <= 0) {
          return;
        }

        this._dispatchEvent('carousel:select', index);

        if (index !== indexBeforeChange || force) {
          this._dispatchEvent('carousel:change', index);
          this._transitionTo(this.items[indexBeforeChange], this.items[index], { animate: animate });
        }
      }

      _transitionTo(fromSlide, toSlide, { animate = true } = {}) {
        if (fromSlide) {
          fromSlide.classList.remove('is-selected');
        }

        toSlide.classList.add('is-selected');

        var target = this._scrollTargetFor(toSlide);

        /* Flag every programmatic scroll, the instant one on load included, so _onCarouselScroll doesn't re-pick
           the index mid-flight. Only when the row will actually move, though: scrollend clears the flag, and an
           unmoved row never fires it. */
        this._hasPendingProgrammaticScroll = Math.abs(target - this.scrollLeft) > 1;
        this.scrollTo({ left: target, behavior: animate ? 'smooth' : 'auto' });
      }

      /* Near either end several cards share the same clamped scroll position, so stepping one index at a time
         could do nothing. Step to the nearest card in that direction whose position actually moves the row. */
      _step(direction) {
        var current = this.scrollLeft;
        var items = direction < 0 ? this.visibleItems.reverse() : this.visibleItems;
        var self = this;
        var target = items.find(function (item) {
          return direction * (self._scrollTargetFor(item) - current) > 1;
        });

        if (target) {
          this.select(this.items.indexOf(target));
        }
      }

      previous() {
        this._step(-1);
      }

      next() {
        this._step(1);
      }

      _onCarouselScroll() {
        this._updateArrows();

        if (this._hasPendingProgrammaticScroll || this.maxScroll <= 0) {
          return;
        }

        var newIndex = this._centredIndex();

        if (newIndex !== -1 && newIndex !== this.selectedIndex) {
          this._selectedIndex = newIndex;
          this._dispatchEvent('carousel:select', newIndex);
          this._dispatchEvent('carousel:change', newIndex);
        }
      }

      _onScrollSettled() {
        super._onScrollSettled();
        this._updateArrows();
      }

      _updateArrows() {
        var maxScroll = this.maxScroll;

        if (this._prevButton) {
          this._prevButton.disabled = maxScroll <= 0 || this.scrollLeft <= 1;
        }

        if (this._nextButton) {
          this._nextButton.disabled = maxScroll <= 0 || this.scrollLeft >= maxScroll - 1;
        }
      }
    };

    window.customElements.define('company-dogs-carousel', CompanyDogsCarousel);
  }

  /* theme.js.liquid is a module and doesn't export ScrollCarousel, so extend it through the registry. */
  window.customElements.whenDefined('scroll-carousel').then(function () {
    defineCarousel(window.customElements.get('scroll-carousel'));
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  /* Re-bind sections the theme editor injects after load. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
